import PropTypes from 'prop-types';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
// routes
import { useRouter } from 'src/routes/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks';
import { createWS } from "src/utils/websocket-client";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const wsRef = useRef(null);
  const reconnectTimeout = useRef(null);
  const lastPing = useRef(Date.now());
  const shouldReconnect = useRef(true);
  const router = useRouter();
  const { logout } = useAuthContext();

  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const retryCount = useRef(0);

  function handleIncomingNotification(data) {
    setNotifications((prev) => {
      const existing = prev.find((n) => n.id === data.id);
      if (existing) {
        return prev.map((n) =>
          n.id === data.id ? { ...n, ...data } : n
        );
      }
      return [data, ...prev];
    });
  }

  useEffect(() => {
    let mounted = true;

    function connect() {
      const ws = createWS("ws/notifications");
      if (!ws) return;
      wsRef.current = ws;

      ws.onopen = () => {
        retryCount.current = 0;
        setConnected(true);
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);

        if (msg.type === "auth_error") {
          console.warn("WS auth error:", msg.detail);

          shouldReconnect.current = false;
          ws.close();
          logout();
          router.replace('/');
          return;
        }

        if (msg.type === "ping") {
          lastPing.current = Date.now();
          ws.send(JSON.stringify({ type: "pong" }));
          return;
        }

        if (msg.type === "connected") {
          return;
        }

        if (msg.type === "notification") {
          handleIncomingNotification(msg.data);
        }
        
        if (msg.type === "snapshot") {
          setNotifications(msg.notifications);
        }
      };

      ws.onclose = (event) => {
        setConnected(false);

        if (event.code === 4001 || !shouldReconnect.current) {
          return;
        }

        retryCount.current += 1;

        if (retryCount.current > 10) {
          console.warn("Too many WS retries, stopping");
          return;
        }

        const delay = Math.min(3000 * 2 ** retryCount.current, 30000);

        if (mounted) {
          reconnectTimeout.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    const heartbeat = setInterval(() => {
      const diff = Date.now() - lastPing.current;

      if (diff > 60000 && wsRef.current) {
        wsRef.current.close();
      }
    }, 15000);

    return () => {
      mounted = false;

      clearTimeout(reconnectTimeout.current);
      clearInterval(heartbeat);

      wsRef.current?.close();
    };
  }, [router, logout]);

    const clear = useCallback(() => {
    setNotifications([]);
    }, []);

    const unresolved = useMemo(() => notifications.filter((n) => !n.resolved_at), [notifications]);

    const value = useMemo(() => ({
    notifications,
    connected,
    unresolved,
    clear,
    }), [notifications, connected, unresolved, clear]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
